/**
 * BrandSwitcher — top-right chip in the shell for tenants where the
 * signed-in admin has grants on more than one org (the Micas / Ideal
 * Laminates tri-org model today; see MULTI_BRAND_DESIGN.md §4.4).
 *
 * Hard invariants:
 *  1. Zero visual impact for every existing tenant. When the caller has
 *     fewer than 2 grants (which is every non-Micas admin today) the
 *     component renders nothing — no wrapper, no whitespace.
 *  2. `activeOrgIds` is modeled as a `number[]` even though the UI here
 *     only picks one. See store/session/reducer.ts for the "why a set"
 *     rationale (future unified-brand view escape hatch).
 *
 * Selection flow:
 *   click → dispatch(setActiveOrgIds([id]))
 *          → slice persists to localStorage under `activeOrgIds`
 *          → interceptor stamps X-Active-Org-Id on the NEXT axios request
 *          → we hard-refresh the page so every already-mounted screen
 *            re-fetches under the new org.
 *
 * NOTE ON THE REFRESH: The Stage C spec called for
 * `dispatch(apiSlice.util.resetApiState())` — that assumed RTK Query,
 * which this project does not use (see product_loyalty_web/package.json
 * — no `@reduxjs/toolkit/query` slice, everything runs on the axios
 * dataProvider). Without RTK Query, in-flight screens keep their stale
 * Redux state from the previous org until they navigate. The simplest
 * correct behavior in this codebase is `window.location.reload()` — it
 * clears every already-rendered screen at once and re-hydrates them with
 * the new active-org header from the very first request. Documented in
 * the PR body as a reversible decision.
 */

import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ChevronDown, Check } from 'lucide-react';
import { Dropdown } from './Dropdown';
import { setActiveOrgIds, OrgGrant } from '../../store/session/reducer';
import type { RootState, AppDispatch } from '../../store';

interface BrandSwitcherProps {
    /** Reload strategy after a switch. Overridable for tests. */
    onSwitch?: () => void;
}

const defaultOnSwitch = () => {
    // Reload after a tick so the reducer's localStorage write settles
    // before the browser tears down the page. In practice the write is
    // synchronous, but the tick is cheap insurance against a race in
    // some browsers.
    setTimeout(() => window.location.reload(), 0);
};

const selectSession = (state: RootState) => state.Session;

const BrandSwitcher: React.FC<BrandSwitcherProps> = ({ onSwitch }) => {
    const dispatch = useDispatch<AppDispatch>();
    const { orgGrants, activeOrgIds } = useSelector(selectSession);

    // Only render for users with 2+ grants. This is the guard the
    // "zero visual impact for everyone else" contract hinges on.
    const activeGrants = useMemo<OrgGrant[]>(
        () => (orgGrants || []).filter((g) => g.isActive !== false),
        [orgGrants]
    );

    const currentBrandName = useMemo(() => {
        if (activeOrgIds.length === 0) return 'Select brand';
        const currentId = activeOrgIds[0];
        const match = activeGrants.find((g) => g.organizationId === currentId);
        return match?.organizationName || 'Select brand';
    }, [activeOrgIds, activeGrants]);

    const handleSelect = useCallback(
        (grant: OrgGrant) => {
            // No-op if already selected — avoid a needless reload.
            if (activeOrgIds.length === 1 && activeOrgIds[0] === grant.organizationId) {
                return;
            }
            dispatch(setActiveOrgIds([grant.organizationId]));
            (onSwitch || defaultOnSwitch)();
        },
        [activeOrgIds, dispatch, onSwitch]
    );

    if (activeGrants.length < 2) {
        return null;
    }

    return (
        <div data-testid="brand-switcher" className="flex items-center">
        <Dropdown className="relative flex items-center h-header">
            <Dropdown.Trigger
                type="button"
                className="inline-flex items-center gap-2 px-3 h-[37.5px] text-sm font-medium rounded-md border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:bg-zink-700 dark:border-zink-500 dark:text-zink-100 dark:hover:bg-zink-600 transition-all"
                aria-label="Switch active brand"
            >
                <span className="text-slate-500 dark:text-zink-300">Brand:</span>
                <span className="font-semibold" data-testid="brand-switcher-current">
                    {currentBrandName}
                </span>
                <ChevronDown className="size-4 text-slate-400" />
            </Dropdown.Trigger>
            <Dropdown.Content
                placement="bottom-end"
                className="absolute z-50 !top-full mt-2 ltr:right-0 rtl:left-0 min-w-[220px] p-1 bg-white rounded-md shadow-lg border border-slate-200 dark:bg-zink-600 dark:border-zink-500"
            >
                <ul role="listbox" aria-label="Brands">
                    {activeGrants.map((grant) => {
                        const isCurrent =
                            activeOrgIds.length === 1 &&
                            activeOrgIds[0] === grant.organizationId;
                        return (
                            <li key={grant.organizationId} role="option" aria-selected={isCurrent}>
                                <button
                                    type="button"
                                    onClick={() => handleSelect(grant)}
                                    data-testid={`brand-switcher-option-${grant.organizationId}`}
                                    className={`w-full flex items-center justify-between gap-3 px-3 py-2 text-sm rounded transition-colors ${
                                        isCurrent
                                            ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 font-semibold'
                                            : 'text-slate-700 dark:text-zink-100 hover:bg-slate-50 dark:hover:bg-zink-500'
                                    }`}
                                >
                                    <span className="truncate">{grant.organizationName}</span>
                                    {isCurrent && <Check className="size-4 flex-shrink-0" />}
                                </button>
                            </li>
                        );
                    })}
                </ul>
            </Dropdown.Content>
        </Dropdown>
        </div>
    );
};

export default BrandSwitcher;
